import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateAccountForm } from "../utils/validators";
import { fetchAddressByCEP } from "../utils/addressService";
import { FinishRegister } from "../../../services/api/Profile/UpdateProfileInfo";
import { ProfileGet } from "../../../services/api/Profile/ProfileGet";
import { ChangeProfileImage } from "../../../services/api/Profile/ChangeProfileImage";
import { notify } from "../../../shared/utils/utils";
import use123zapStore from "../../../context/123zapStore";
import { useAuth } from "../../../app/providers/AuthProvider";

export const useAccountSetup = () => {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const userPicture = user?.picture || "";
  const setFinishedRegister = use123zapStore((state) => state.setFinishedRegister);
  const loadPlanoAtual = use123zapStore((state) => state.loadPlanoAtual);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(userPicture);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    person_type: "PJ",
    firstName: "",
    lastName: "",
    cpf_cnpj: "",
    telephone: "",
    cellphone: "",
    email: "",
    email_cc: "",
    zipcode: "",
    street: "",
    number: "",
    neighborhood: "",
    city: "",
    state: "",
    plan_id: 10,
  });

  /* =========================
     CARREGAR DADOS INICIAIS
  ========================= */
  const onlyNumbers = (value) =>
    value ? String(value).replace(/\D/g, "") : "";

  const getProfileImageErrorMessage = (response) => {
    const message =
      typeof response === "string" ? response : response?.message || response?.error;

    if (String(message).toLowerCase().includes("invalid image format")) {
      return "Formato de imagem inválido.";
    }

    return "Erro ao atualizar foto.";
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await ProfileGet();

        if (response?.status === "success") {
          const fullName = response.name || "";
          const nameParts = fullName.split(" ");

          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          setFormData((prev) => ({
            ...prev,
            firstName,
            lastName,
            email: response.email ?? "",
            cpf_cnpj: response.cpf_cnpj ?? "",
            telephone: onlyNumbers(response.address?.telephone),
            cellphone: onlyNumbers(response.address?.cellphone),
            zipcode: response.address?.postal_code ?? "",
            street: response.address?.street ?? "",
            number: response.address?.number ?? "",
            neighborhood: response.address?.neighborhood ?? "",
            city: response.address?.city ?? "",
            state: response.address?.state ?? "",
          }));

          setProfileImage(
            response.picture ?? response.avatar ?? response.profile_image ?? userPicture
          );
        }
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadProfile();
  }, [userPicture]);

  /* =========================
     HANDLE CHANGE
  ========================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  /* =========================
     BUSCAR CEP
  ========================= */

  const handleCEPBlur = async () => {
    const address = await fetchAddressByCEP(formData.zipcode);

    if (address) {
      setFormData((prev) => ({
        ...prev,
        ...address,
      }));

      setErrors((prev) => {
        const nextErrors = { ...prev };

        Object.entries(address).forEach(([fieldName, fieldValue]) => {
          if (fieldValue) {
            delete nextErrors[fieldName];
          }
        });

        return nextErrors;
      });
    }
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notify("Error", "Selecione uma imagem válida.", true);
      return;
    }

    const reader = new FileReader();

    reader.onload = async () => {
      const base64Image = reader.result;

      try {
        setImageLoading(true);

        const response = await ChangeProfileImage(base64Image);

        if (response?.status === "success") {
          setProfileImage(base64Image);
          await refreshUser(true);
          notify("Success", "Foto atualizada com sucesso.", true);
          return;
        }

        notify("Error", getProfileImageErrorMessage(response), true);
      } catch (error) {
        console.error("Erro ao atualizar foto:", error);
        notify("Error", "Erro ao atualizar foto.", true);
      } finally {
        setImageLoading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateAccountForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await FinishRegister(formData);

      if (response.status === "success") {
        setFinishedRegister(true);
        await refreshUser(true);
        await loadPlanoAtual(true);
        notify("Success", "Cadastro finalizado com sucesso!", true);
        navigate("/");
      } else {
        notify("Error", "Erro ao finalizar cadastro.", true);
      }
    } catch (error) {
      console.error("Erro ao finalizar cadastro:", error);
      notify("Error", "Erro ao finalizar cadastro.", true);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    errors,
    loading,
    initialLoading,
    imageLoading,
    profileImage,
    isPJ: formData.person_type === "PJ",
    handleChange,
    handleSubmit,
    handleCEPBlur,
    handleProfileImageChange,
  };
};
